/**
 * Auth provider — wraps the dashboard, exposes the current session and
 * sign-out via context. Reads any existing session from sessionStorage on
 * mount so a refresh doesn't bounce the user back to the login screen.
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { clearSession, getSession, type AuthSession } from './google';

interface AuthContextValue {
  session: AuthSession | null;
  setSession: (s: AuthSession | null) => void;
  signOut: () => void;
}

const Ctx = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used within AuthProvider');
  return v;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSessionState] = useState<AuthSession | null>(() => getSession());

  useEffect(() => {
    if (!session) return;
    // Auto-expire session when the JWT's `exp` is reached.
    const ms = session.expiresAt - Date.now();
    if (ms <= 0) {
      setSessionState(null);
      return;
    }
    const t = window.setTimeout(() => setSessionState(null), ms);
    return () => window.clearTimeout(t);
  }, [session]);

  const setSession = useCallback((s: AuthSession | null) => {
    setSessionState(s);
  }, []);

  const signOut = useCallback(() => {
    clearSession();
    setSessionState(null);
  }, []);

  return (
    <Ctx.Provider value={{ session, setSession, signOut }}>
      {children}
    </Ctx.Provider>
  );
}
